import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useReveal } from "@/hooks/use-reveal";
import { Shield, Lock, Server, EyeOff, Fingerprint, Code2 } from "lucide-react";

const PRACTICES = [
  {
    icon: Lock,
    title: "PBKDF2-hashed PIN",
    summary:
      "Your four-digit PIN is never stored as plain text. We use PBKDF2 with 100,000 iterations and a unique random salt per account, so even if two people choose the same PIN their hashes are completely different.",
  },
  {
    icon: Shield,
    title: "Brute-force lockout",
    summary:
      "After five failed PIN attempts the app pauses for five minutes. Every incorrect guess also adds a short server-side delay, making automated guessing impractical.",
  },
  {
    icon: Server,
    title: "Row-level access control",
    summary:
      "Every database request is authenticated and scoped to your user ID. The rules are enforced by the database itself, so one account cannot read another's businesses, entries, journal or contacts.",
  },
  {
    icon: EyeOff,
    title: "No ads, no data resale",
    summary:
      "We do not sell your data, share it with advertisers, or use your entries to train models. Analytics, where used, are aggregate and never include entry content.",
  },
  {
    icon: Fingerprint,
    title: "Encrypted in transit",
    summary:
      "All traffic between your device and our backend uses TLS. Account credentials and session tokens are handled by the same authentication infrastructure we use for sign-in.",
  },
];

export const Route = createFileRoute("/security")({
  head: () => ({
    meta: [
      { title: "Security — how The Founders App protects your data" },
      {
        name: "description",
        content:
          "The Founders App protects your business data with PBKDF2 PIN hashing, brute-force lockouts, row-level access rules and encrypted transit. Operated by Skylab Studio LLC.",
      },
      { property: "og:title", content: "Security — how The Founders App protects your data" },
      {
        property: "og:description",
        content:
          "PBKDF2-hashed PINs, brute-force lockouts, row-level access control and encrypted transit.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://thefoundersapp.com/security" },
      { property: "og:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://thefoundersapp.com/security" }],
  }),
  component: Security,
});

function Security() {
  useReveal();

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="aurora" aria-hidden />
      <SiteHeader />

      <main id="main" className="relative mx-auto w-full max-w-4xl px-6 pt-16 pb-24 sm:px-10">
        <p className="reveal micro-label">Security</p>
        <h1 className="reveal etched mt-5 text-[2.5rem] leading-[0.95] font-semibold sm:text-6xl [--d:80ms]">
          Built like a vault, calm like a notebook.
        </h1>
        <p className="reveal mt-5 max-w-2xl text-sm leading-relaxed text-muted-foreground [--d:120ms]">
          The Founders App holds your most private business thinking. We take that seriously: your data is
          isolated by account, hashed with modern standards, and guarded against automated guessing.
        </p>

        <div className="mt-14 grid gap-5 sm:grid-cols-2">
          {PRACTICES.map((p, i) => (
            <section
              key={p.title}
              className="reveal soft-panel p-7"
              style={{ ["--d" as string]: `${140 + i * 70}ms` }}
            >
              <div className="flex items-center gap-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-sm border border-border bg-surface text-muted-foreground">
                  <p.icon className="h-4 w-4" />
                </span>
                <h2 className="text-base font-medium">{p.title}</h2>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{p.summary}</p>
            </section>
          ))}
        </div>

        <section className="reveal soft-panel mt-5 p-8 [--d:520ms]">
          <h2 className="text-base font-medium">What we do not do</h2>
          <ul className="mt-5 space-y-3 text-sm text-muted-foreground">
            <li className="flex gap-3">
              <span className="text-muted-foreground/50">—</span>
              We do not store your PIN in plain text or reversible encryption.
            </li>
            <li className="flex gap-3">
              <span className="text-muted-foreground/50">—</span>
              We do not allow unlimited PIN guesses — the lockout slows and then stops attempts.
            </li>
            <li className="flex gap-3">
              <span className="text-muted-foreground/50">—</span>
              We do not let staff or admins read your entries. Even the site admin sees only
              aggregate counts and billing metadata, never business content.
            </li>
            <li className="flex gap-3">
              <span className="text-muted-foreground/50">—</span>
              We do not share your contacts, journal or daily entries with third parties.
            </li>
          </ul>
        </section>

        <section className="reveal soft-panel mt-5 p-8 [--d:600ms]">
          <div className="flex items-center gap-4">
            <span className="flex h-9 w-9 items-center justify-center rounded-sm border border-border bg-surface text-muted-foreground">
              <Code2 className="h-4 w-4" />
            </span>
            <h2 className="text-base font-medium">AI-assisted, human-verified</h2>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            This app was built with AI-assisted development tools, but every security-critical path —
            authentication, data isolation, encryption, and rate limiting — was manually reviewed,
            tested, and verified before shipping. A deep security scan of the backend was run and
            passed with zero critical or high findings. Ongoing automated scans continue to check the
            codebase for newly introduced issues.
          </p>
        </section>

        <section className="reveal soft-panel mt-5 p-8 [--d:680ms]">
          <h2 className="text-base font-medium">Responsible disclosure</h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            If you find a security issue, please tell us before disclosing it publicly. We will
            investigate quickly and keep you informed. Reach us through the contact details on the{" "}
            <a href="/faq" className="underline underline-offset-4 hover:text-foreground">
              FAQ
            </a>{" "}
            page.
          </p>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
